// utils/cartHelpers.ts

import type { ProductWithVariants } from '@/types/cart';

/**
 * Transform a product response to ProductWithVariants format
 */
export function transformProductToVariants(product: any): ProductWithVariants {
  return {
    _id: product._id || product.id,
    id: product.id || product._id,
    name: product.name,
    description: product.description,
    price: product.price || product.pricing?.originalPrice || 0,
    discount: product.discount || product.discountInfo || product.pricing?.discountInfo,
    discountedPrice: product.discountedPrice || product.discount?.discountedPrice || product.pricing?.discountInfo?.discountedPrice,
    originalPrice: product.originalPrice || product.price || product.pricing?.originalPrice,
    discountPercentage: product.discountPercentage || product.discount?.value || product.discountInfo?.value,
    
    category: product.category,
    subCategory: product.subCategory,
    brand: product.brand,
    
    images: product.images || [],
    variants: product.variants || [],
    
    stock: product.stock || product.inventory?.currentStock || 0,
    lowStockAlert: product.lowStockAlert || product.inventory?.lowStockAlert || 5,
    sku: product.sku || product.inventory?.sku || '',
    upc: product.upc || '',
    condition: product.condition || 'new',
    
    weight: product.weight || product.shippingInfo?.weight?.value || 1,
    minimumPurchaseQuantity: product.minimumPurchaseQuantity || 1,
    
    seller: product.seller,
    
    shippingInfo: product.shippingInfo || product.shippingDetails,
    returnPolicy: product.returnPolicy,
    pricing: product.pricing,
    
    visibility: product.visibility || 'visible',
    status: product.status || 'active',
  };
}

/**
 * Check if a product has variants
 */
export function hasProductVariants(product: any): boolean {
  return Boolean(product?.variants && Array.isArray(product.variants) && product.variants.length > 0);
}

/**
 * Check if a variant has sub-variants
 */
export function hasSubVariants(variant: any): boolean {
  return Boolean(variant?.subVariants && Array.isArray(variant.subVariants) && variant.subVariants.length > 0);
}

/**
 * Check if a sub-variant has sub-sub-variants
 */
export function hasSubSubVariants(subVariant: any): boolean {
  return Boolean(subVariant?.subSubVariants && Array.isArray(subVariant.subSubVariants) && subVariant.subSubVariants.length > 0);
}

/**
 * Get variant hierarchy depth
 */
export function getVariantDepth(product: ProductWithVariants): number {
  if (!hasProductVariants(product)) return 0;
  
  const firstVariant = product.variants[0];
  if (!hasSubVariants(firstVariant)) return 1;
  
  const firstSubVariant = firstVariant.subVariants[0];
  if (!hasSubSubVariants(firstSubVariant)) return 2;
  
  return 3;
}

/**
 * Get all unique variant names at each level
 */
export function getVariantLevelNames(product: ProductWithVariants): {
  level1?: string;
  level2?: string;
  level3?: string;
} {
  if (!hasProductVariants(product)) return {};
  
  const level1 = product.variants[0]?.name;
  const level2 = hasSubVariants(product.variants[0]) 
    ? product.variants[0].subVariants[0]?.name 
    : undefined;
  const level3 = level2 && hasSubSubVariants(product.variants[0].subVariants[0])
    ? product.variants[0].subVariants[0].subSubVariants[0]?.name
    : undefined;
  
  return { level1, level2, level3 };
}